import React, { useState, useEffect } from 'react';
import { 
  FolderOpen, FileText, Download, ExternalLink,
  Users, CreditCard, BookOpen, ClipboardList, RefreshCw, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KitSoci() {
  const [soci, setSoci] = useState([]);
  const [selectedSocio, setSelectedSocio] = useState('');
  const [selectedQuota, setSelectedQuota] = useState('');
  const [quoteSocio, setQuoteSocio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const documenti = [
    {
      categoria: 'Moduli di Iscrizione',
      icon: Users,
      color: 'text-blue-600',
      items: [
        {
          id: 'domanda_ammissione',
          nome: 'Domanda di Ammissione a Socio',
          descrizione: 'Modulo standard per la richiesta di ammissione come socio ordinario',
          personalizzabile: true
        },
        {
          id: 'scheda_anagrafica_socio',
          nome: 'Scheda Anagrafica Socio',
          descrizione: 'Modulo per la raccolta dei dati anagrafici completi',
          personalizzabile: true
        },
        {
          id: 'informativa_privacy_soci',
          nome: 'Informativa Privacy Soci',
          descrizione: 'Informativa sul trattamento dei dati personali ai sensi del GDPR',
          personalizzabile: false
        }
      ]
    },
    {
      categoria: 'Documentazione Assemblee',
      icon: ClipboardList,
      color: 'text-purple-600',
      items: [
        {
          id: 'convocazione_assemblea',
          nome: 'Convocazione Assemblea Soci',
          descrizione: 'Fac-simile per la convocazione dell\'assemblea ordinaria/straordinaria',
          personalizzabile: false
        },
        {
          id: 'verbale_assemblea',
          nome: 'Verbale Assemblea Soci',
          descrizione: 'Modello per la redazione del verbale assembleare',
          personalizzabile: false
        },
        {
          id: 'foglio_presenze_assemblea',
          nome: 'Foglio Presenze Assemblea',
          descrizione: 'Registro firme per la verifica del quorum',
          personalizzabile: false
        },
        {
          id: 'delega_assemblea',
          nome: 'Delega Partecipazione Assemblea',
          descrizione: 'Modulo per delegare altro socio alla partecipazione',
          personalizzabile: true
        }
      ]
    },
    {
      categoria: 'Quote e Pagamenti',
      icon: CreditCard,
      color: 'text-green-600',
      items: [
        {
          id: 'ricevuta_quota',
          nome: 'Ricevuta Quota Associativa',
          descrizione: 'Ricevuta per il pagamento della quota annuale',
          personalizzabile: true
        },
        {
          id: 'sollecito_quota',
          nome: 'Sollecito Pagamento Quota',
          descrizione: 'Lettera di sollecito per quote non pagate',
          personalizzabile: true
        }
      ]
    },
    {
      categoria: 'Gestione Soci',
      icon: BookOpen,
      color: 'text-amber-600',
      items: [
        {
          id: 'lettera_accettazione',
          nome: 'Lettera Accettazione Socio',
          descrizione: 'Comunicazione di accoglimento della domanda di ammissione',
          personalizzabile: true
        },
        {
          id: 'tessera_socio',
          nome: 'Tessera Socio (Template)',
          descrizione: 'Modello per la stampa delle tessere associative',
          personalizzabile: true
        },
        {
          id: 'comunicazione_esclusione',
          nome: 'Comunicazione Esclusione Socio',
          descrizione: 'Lettera per la comunicazione di esclusione/recesso',
          personalizzabile: true
        },
        {
          id: 'richiesta_recesso',
          nome: 'Richiesta Recesso Socio',
          descrizione: 'Modulo per la richiesta volontaria di recesso',
          personalizzabile: true
        }
      ]
    }
  ];

  useEffect(() => {
    fetchSoci();
  }, []);

  // Carica le quote quando si seleziona un socio
  useEffect(() => {
    if (selectedSocio && selectedSocio !== 'none') {
      fetchQuoteSocio(selectedSocio);
    } else {
      setQuoteSocio([]);
      setSelectedQuota('');
    }
  }, [selectedSocio]);

  const fetchSoci = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSoci(data.soci || []);
      }
    } catch (error) {
      console.error('Errore caricamento soci:', error);
    }
  };

  const fetchQuoteSocio = async (personaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/quote/lista?persona_id=${personaId}&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuoteSocio(data.quote || []);
        // Se c'è solo una quota, selezionala automaticamente
        if (data.quote?.length === 1) {
          setSelectedQuota(data.quote[0].quota_id);
        }
      }
    } catch (error) {
      console.error('Errore caricamento quote:', error);
    }
  };

  const handleDownload = async (templateId, personalizzabile) => {
    setDownloadingId(templateId);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/kit-soci/genera/${templateId}`;
      
      // Build query params
      const params = new URLSearchParams();
      
      // Aggiungi persona_id se selezionato e il documento è personalizzabile
      if (selectedSocio && selectedSocio !== 'none' && personalizzabile) {
        params.append('persona_id', selectedSocio);
      }
      
      // Per la ricevuta quota, aggiungi quota_id se selezionato
      if (templateId === 'ricevuta_quota' && selectedQuota && selectedQuota !== 'none') {
        params.append('quota_id', selectedQuota);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `${templateId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(urlBlob);
        toast.success('Documento scaricato con successo');
      } else {
        toast.error('Errore nel download del documento');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="kit-soci-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FolderOpen className="h-6 w-6" />
          Kit Amministrativo Soci
        </h1>
        <p className="text-gray-500 mt-1">
          Modulistica e documenti per la gestione dei soci dell'associazione
        </p>
      </div>

      {/* Info Card with Socio Selector */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start gap-4">
            <FileText className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">Documenti Personalizzabili</h3>
              <p className="text-sm text-blue-700 mt-1">
                I documenti vengono automaticamente compilati con i dati del tuo ente. 
                Seleziona un socio per personalizzare anche i documenti individuali.
              </p>
            </div>
            <div className="w-full md:w-64">
              <label className="text-sm font-medium text-blue-900 block mb-2">
                Personalizza per socio:
              </label>
              <Select value={selectedSocio} onValueChange={setSelectedSocio}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Seleziona socio (opzionale)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno (modello generico)</SelectItem>
                  {soci.map((socio) => (
                    <SelectItem key={socio.persona_id} value={socio.persona_id}>
                      {socio.cognome} {socio.nome} - {socio.numero_tessera || 'N/D'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Selettore Quota (visibile solo se socio selezionato e ci sono quote) */}
          {selectedSocio && selectedSocio !== 'none' && quoteSocio.length > 0 && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="flex flex-col md:flex-row items-start gap-4">
                <Receipt className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900">Ricevuta Quota Associativa</h4>
                  <p className="text-sm text-blue-700">
                    Per generare la ricevuta, seleziona la quota da documentare.
                  </p>
                </div>
                <div className="w-full md:w-64">
                  <label className="text-sm font-medium text-blue-900 block mb-2">
                    Seleziona quota:
                  </label>
                  <Select value={selectedQuota} onValueChange={setSelectedQuota}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Seleziona quota" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna quota</SelectItem>
                      {quoteSocio.map((quota) => (
                        <SelectItem key={quota.quota_id} value={quota.quota_id}>
                          Anno {quota.anno} - €{quota.importo} ({quota.stato || 'pagata'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Categories */}
      <div className="grid gap-6">
        {documenti.map((categoria) => (
          <Card key={categoria.categoria}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <categoria.icon className={`h-5 w-5 ${categoria.color}`} />
                {categoria.categoria}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {categoria.items.map((doc) => (
                  <div 
                    key={doc.id}
                    className="flex items-start gap-4 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900">{doc.nome}</h4>
                      <p className="text-sm text-gray-500 mt-1">{doc.descrizione}</p>
                      {doc.personalizzabile && selectedSocio && selectedSocio !== 'none' && (
                        <span className="inline-block mt-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          Personalizzato per socio selezionato
                        </span>
                      )}
                      {/* Messaggio speciale per ricevuta quota */}
                      {doc.id === 'ricevuta_quota' && selectedSocio && selectedSocio !== 'none' && selectedQuota && selectedQuota !== 'none' && (
                        <span className="inline-block mt-2 ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          + Quota selezionata
                        </span>
                      )}
                      {doc.id === 'ricevuta_quota' && selectedSocio && selectedSocio !== 'none' && (!selectedQuota || selectedQuota === 'none') && quoteSocio.length > 0 && (
                        <span className="inline-block mt-2 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                          Seleziona una quota sopra per personalizzare
                        </span>
                      )}
                      <Button 
                        variant="link" 
                        className="p-0 h-auto mt-2 text-blue-600"
                        onClick={() => handleDownload(doc.id, doc.personalizzabile)}
                        disabled={downloadingId === doc.id}
                      >
                        {downloadingId === doc.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            Generazione...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-1" />
                            Scarica PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer Note */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="text-center text-sm text-gray-600">
            <p>
              <strong>Nota:</strong> I documenti sono forniti come modelli di riferimento conformi alla normativa ETS. 
              Si consiglia di verificare la conformità con lo statuto della propria associazione.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
